import styled from '@emotion/styled';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import H1 from '@base/H1/H1';
import H4 from '@base/H4/H4';
import H5 from '@base/H5/H5';
import P1 from '@base/P1/P1';
import Image from '@base/Image';
import { useIsMobile } from '@stores/AppCommonData';
import Button from 'aquilla/core/molecules/Button';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import LottieAnimation from '@base/LottieAnimation/LottieAnimation';
import { getAnimationPath } from '@utils/animationHelpers';
import LinkIcon from '@icon/Referrals/Link';
import MUIDialog from '@material-ui/core/Dialog';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { getImage } from '@utils/imageHelper';
import Close from 'aquilla/icons/actions/Close';
import { useEffect, useState } from 'react';
import Typography from 'aquilla/core/atoms/Typography';
import ShareIcon from '@icon/Referrals/Share';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import { PAID_USER, REFERRAL_REWARD_KNOW_MORE } from '../constants';
import ReferrlRewards from './ReferralRewards';
import RewardDetail from './RewardDetails';
import { getDiscountPercentData } from './apiHelper';

const HeroBgWrapper = styled.div`
  background-color: var(--color-orange-1);
  height: 592px;
  ${mediaQuery.uptoMobile} {
    height: 350px;
  }
`;

const HeroWrapper = styled.div`
  display: flex;
  position: relative;
  margin: auto;
  max-width: 1136px;
  justify-content: space-between;
  align-items: center;
  ${mediaQuery.onlyTablet} {
    max-width: 100%;
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  }
`;

const LeftWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-basis: 39%;
  margin: ${Spacings.SPACING_14B} ${Spacings.SPACING_0B} ${Spacings.SPACING_8B}
    ${Spacings.SPACING_0B};
  ${mediaQuery.uptoMobile} {
    flex-basis: 100%;
    margin: ${Spacings.SPACING_8B} ${Spacings.SPACING_0B} ${Spacings.SPACING_5B}
      ${Spacings.SPACING_0B};
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
    gap: ${Spacings.SPACING_2B};
  }
`;

const RightWrapper = styled.div`
  ${mediaQuery.uptoMobile} {
    width: ${Spacings.SPACING_30B};
    height: ${Spacings.SPACING_28B};
    border-radius: 7px;
    flex: none;
    order: 1;
    flex-grow: ${Spacings.SPACING_0B};
  }
`;

const LaptopImageWrapper = styled.div`
  position: relative;
  height: 264px;
  width: 480px;
  ${mediaQuery.uptoMobile} {
    width: ${Spacings.SPACING_30B};
    height: ${Spacings.SPACING_28B};
  }
  ${mediaQuery.onlyTablet} {
    height: 212px;
    width: 380px;
  }
`;

const HeroHeading = styled(H1)`
  color: var(--color-brown-1);
  line-height: ${Spacings.SPACING_14B};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H3};
    line-height: ${Spacings.SPACING_8B};
  }
  ${mediaQuery.onlyTablet} {
    line-height: unset;
  }
`;

const HeroSubheading = styled(H5)`
  color: var(--color-brown-2);
  ${mediaQuery.aboveMobile} {
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const FreeSubHeading = styled(HeroSubheading)`
  ${mediaQuery.uptoMobile} {
    font-weight: ${Fonts.LIGHT};
    font-size: ${Fonts.H6};
  }
  ${mediaQuery.aboveMobile} {
    margin-top: ${Spacings.SPACING_0B};
  }
`;

const ReferralCodeBlockWrapper = styled.div`
  background-color: var(--color-base-1);
  padding: ${Spacings.SPACING_4B};
  border-radius: ${Spacings.SPACING_2B};
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  width: 100%;
  margin-top: ${Spacings.SPACING_8B};
  ${mediaQuery.uptoMobile} {
    position: absolute;
    bottom: -46%;
    padding: ${Spacings.SPACING_4B};
    flex-direction: column;
    align-items: start;
    gap: ${Spacings.SPACING_2B};
    width: calc(100% - ${Spacings.SPACING_8B});
  }
`;

const ReferralCodeContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const ReferralCodeWrapper = styled.div`
  ${mediaQuery.uptoMobile} {
    text-align: left;
  }
`;

const ReferralCode = styled(H4)`
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_1B};
  }
`;

const ReferralCodeHelper = styled(P1)``;

const ActionButtonContainer = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_4B};
`;

const StyButton = styled(Button)`
  ${mediaQuery.aboveMobile} {
    width: 108px;
    padding: 0;

    & .button--right-icon {
      height: ${Spacings.SPACING_6B};
    }
  }
`;

const ReferralRewardblock = styled.div`
  position: relative;
  margin: auto;
  max-width: 1136px;
  ${mediaQuery.uptoMobile} {
    margin-left: ${Spacings.SPACING_4B};
  }
`;

const SocialProofContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
  ${mediaQuery.aboveMobile} {
    margin-top: ${Spacings.SPACING_4B};
  }
  ${mediaQuery.uptoMobile} {
    font-weight: ${Fonts.LIGHT};
    font-size: ${Fonts.SMALL};
    flex-direction: column;
    align-items: baseline;
  }
`;

const AnimationContainer = styled(LottieAnimation)`
  width: ${Spacings.SPACING_15B};
  height: ${Spacings.SPACING_6B};
  flex-shrink: 0;
`;

const Mask = styled.span`
  color: var(--color-text-secondary);
`;

const StyDialog = styled(MUIDialog)`
  &.MuiDialog-root .MuiDialog-container .MuiPaper-root {
    bottom: 0;
    background: var(--color-base-0);
    border-radius: ${Spacings.SPACING_2B};
    max-height: none;
    width: 500px;
    padding: ${Spacings.SPACING_6B};
    box-shadow: ${boxShadowGenerator({ color: 'rgba(0, 0, 0, 0.12)' })};
    background-image: url('${getImage('referrals/shareBg.svg')}');
    background-repeat: no-repeat;
    background-position: right top;
  }
`;

const ShareText = styled.div`
  margin: ${Spacings.SPACING_6B} 0;
`;

const ShareContainer = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_3B};
  margin-top: ${Spacings.SPACING_3B};
`;

const IconContainer = styled.div`
  width: ${Spacings.SPACING_12B};
  height: ${Spacings.SPACING_12B};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 1px solid var(--color-divider);
  cursor: pointer;
`;

const shareConfig = [
  {
    id: 'facebook',
    icon: '/referrals/social/fb.png'
  },
  {
    id: 'twitter',
    icon: '/referrals/social/twitter.png'
  },
  {
    id: 'whatsapp',
    icon: 'referrals/social/whatsapp.png'
  },
  {
    id: 'telegram',
    icon: '/referrals/social/telegram.png'
  }
];

const ReferralCodeBlock = ({
  referralCode,
  shareLink,
  referNow,
  setReferralClicked
}) => {
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const [showPopup, setShowPopup] = useState(false);

  const [discountPercent, setDiscountPercent] = useState('5%');

  useEffect(() => {
    getDiscountPercentData().then((data) => {
      setDiscountPercent(
        data?.experimentValue?.userReferralDiscountPercent || '5%'
      );
    });
  }, []);

  const share = () => {
    localStorage.setItem('redirect', 'true');
    setReferralClicked(true);
    setShowPopup(true);
  };

  const onCopyClick = () => {
    localStorage.setItem('redirect', 'true');
    setReferralClicked(true);
    shareLink('copy');
  };
  const closePopup = () => setShowPopup(false);

  return (
    <>
      <ReferralCodeContainer>
        <ReferralCodeWrapper>
          <ReferralCodeHelper>
            {isLoggedIn
              ? 'Your referral code'
              : 'Login to view your referral code'}
          </ReferralCodeHelper>
          <ReferralCode color="var(--color-text-primary)">
            {isLoggedIn ? referralCode : <Mask>XXXXXXXX</Mask>}
          </ReferralCode>
        </ReferralCodeWrapper>
        <ActionButtonContainer>
          {isLoggedIn && (
            <>
              <StyButton
                label="Copy"
                variant="secondary"
                size="large"
                startIcon={<LinkIcon width={24} height={24} />}
                onClick={onCopyClick}
              />
              <StyButton
                label="Share"
                size="large"
                startIcon={<ShareIcon width={24} height={24} />}
                onClick={share}
              />
            </>
          )}
          {!isLoggedIn && (
            <StyButton label="Refer now" size="large" onClick={referNow} />
          )}
        </ActionButtonContainer>
      </ReferralCodeContainer>
      <StyDialog open={showPopup}>
        <Close onClick={closePopup} />
        <ShareText>
          <Typography variant="h3">Share your referral code</Typography>
          <Typography variant="h3">{referralCode}</Typography>
        </ShareText>
        <Typography variant="p2" color="var(--color-text-secondary)">
          Your friends get {discountPercent} with your referral
        </Typography>
        <ShareContainer>
          {shareConfig.map(({ id, icon }) => {
            return (
              <IconContainer key={id}>
                <Image
                  width={32}
                  height={32}
                  src={icon}
                  alt={id}
                  onClick={() => shareLink(id)}
                />
              </IconContainer>
            );
          })}
          <IconContainer
            key="copy"
            onClick={() => {
              shareLink('copy');
              closePopup();
            }}
          >
            <LinkIcon width={24} height={24} />
          </IconContainer>
        </ShareContainer>
      </StyDialog>
    </>
  );
};

const HeroCard = ({
  heroCardData,
  referralCode,
  isWon,
  userType,
  shareLink,
  referNow,
  setReferralClicked,
  rewardDetails
}) => {
  const isMobile = useIsMobile();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const {
    extraBlockInfo: { subHeader, header, iconUrl: sourceIconUrl } = {},
    availableRewards
  } = heroCardData ?? {};
  const isPaid = userType === PAID_USER;

  let iconUrl = sourceIconUrl;
  if (isMobile) {
    if (isPaid) iconUrl = 'referrals/laptopBanner.svg';
    else iconUrl = 'referrals/freeUserHeroBanner.svg';
  }

  const [drawerState, setDrawerState] = useState(false);
  const selectedGoalUID = useSelectedGoalUID();
  const goalSegmentData = useGoalEventProps({ goalUID: selectedGoalUID });
  const closeRewardDetailDrawer = () => {
    setDrawerState(false);
  };

  const handleKnowMore = () => {
    setDrawerState(true);
    segment.track(REFERRAL_REWARD_KNOW_MORE, {
      ...goalSegmentData,
      referral_type: userType
    });
  };

  return (
    <>
      <HeroBgWrapper className="hero-wrapper">
        <HeroWrapper>
          <LeftWrapper>
            <HeroHeading>{header}</HeroHeading>
            <SocialProofContainer>
              {!isLoggedIn && (
                <AnimationContainer
                  src={getAnimationPath('goalPage/joiningMembers.json')}
                />
              )}
              {isLoggedIn ? (
                <HeroSubheading>{subHeader}</HeroSubheading>
              ) : (
                <FreeSubHeading>{subHeader}</FreeSubHeading>
              )}
            </SocialProofContainer>

            {!isMobile && (
              <ReferralCodeBlockWrapper>
                <ReferralCodeBlock
                  referralCode={referralCode}
                  shareLink={shareLink}
                  referNow={referNow}
                  setReferralClicked={setReferralClicked}
                />
              </ReferralCodeBlockWrapper>
            )}
          </LeftWrapper>
          <RightWrapper>
            <LaptopImageWrapper>
              <Image src={iconUrl} alt="laptop" layout="fill" />
            </LaptopImageWrapper>
          </RightWrapper>
        </HeroWrapper>
        {availableRewards && (
          <ReferralRewardblock>
            <ReferrlRewards
              referralRewards={availableRewards}
              isWon={isWon}
              isPaid={isPaid}
              handleKowMore={handleKnowMore}
            />
          </ReferralRewardblock>
        )}
      </HeroBgWrapper>
      <RewardDetail
        drawerData={rewardDetails}
        drawerState={drawerState}
        closeRewardDetailDrawer={closeRewardDetailDrawer}
      />
    </>
  );
};

export default HeroCard;
